import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  ReactNode,
} from "react";
import "./ServerDataGrid.css";
import {
  DataGridPremium,
  GridColDef,
  GridRowModes,
  GridRowId,
  useGridApiRef,
  GridActionsCellItem,
  GridRowParams,
  GridColumnMenuProps,
  GridGetRowsError,
  GridUpdateRowError,
  GridInitialState,
  DataGridPremiumProps,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridRowSelectionModel,
  GridFilterModel,
} from "@mui/x-data-grid-premium";
import { Box, Alert, Snackbar } from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { CustomToolbar } from "./CustomToolbar";
import { GroupPaginationCell } from "./GroupPaginationCell";
import { CustomColumnMenu } from "./ColumnMenu";
import {
  SelectOption,
  BulkStringOperationConfig,
  AdvancedFilterModel,
  countAdvancedFilterItems,
} from "./types";
import { CombinedFilterPanel } from "./components/CombinedFilterPanel";
import {
  BaseContextMenu,
  BaseContextMenuProps,
} from "./components/BaseContextMenu";
import { DataGridUrlStateManager } from "./DataGridUrlStateManager";
import { CreateFormModal, FormField } from "./CreateFormModal";
import { OptionsContext, OptionsContextValue } from "./OptionsContext";
import { useIsFirstRender } from "../hooks/isFirstRender";
import {
  useDebouncedSearch,
  useGridUrlSync,
  useCreateFormModal,
  useRowEditing,
  useRowSelection,
  useServerDataSource,
} from "./hooks";
import { useDataGridSettings } from "./DataGridSettings";

interface ServerDataGridProps {
  initialStateOverride?: GridInitialState;
  columns: GridColDef[];
  apiEndpoint: string;
  tableName: string;
  enableCreate?: boolean;
  enableEdit?: boolean;
  enableDelete?: boolean;
  enableSearch?: boolean;
  enableDuplicate?: boolean;
  createFormFields?: FormField[];
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  height?: number | string;
  getRowId?: (row: any) => string | number;
  onError?: (error: Error) => void;
  onSuccess?: (message: string) => void;
  columnMenuConfig?: GridColumnMenuProps;
  getDetailPanelContent?: (
    params: GridRowParams,
    apiRef: React.RefObject<any>
  ) => ReactNode;
  columnVisibilityModel?: Record<string, boolean>;
  submitOnlyChangedFields?: boolean;
  onBeforeUpdate?: (
    originalRow: any,
    updatedRow: any,
    changedFields: Record<string, any>
  ) => any;
  onCreateClick?: () => void;
  onDuplicateClick?: (selectedRow: any) => void;
  ExtraButtonsPanel?: React.ComponentType<{
    rowSelectionModel: GridRowSelectionModel;
    setSnackbar: (snackbar: {
      open: boolean;
      message: string;
      severity: "success" | "error";
    }) => void;
    apiRef: React.RefObject<any>;
    getSelectedRows: () => any[];
    onBulkUpdate: (
      ids: (string | number)[],
      field: string,
      operations: BulkStringOperationConfig[]
    ) => Promise<{ success: boolean; error?: string }>;
  }>;
  extraButtonsPanelProps?: Record<string, any>;
  apiRef?: React.RefObject<any>;
  externalSearchValue?: string;
  ContextMenuComponent?: React.ComponentType<BaseContextMenuProps>;
  defaultEditFieldVisibility?: Record<string, boolean>;
  // Row Grouping Configuration
  enableRowGrouping?: boolean;
  defaultRowGroupingModel?: string[];
  rowGroupingColumnMode?: "single" | "multiple";
}

const apiBase = process.env.REACT_APP_BACKEND_API_URL;

/**
 * Transforms nested table-column structure to flat dot-notation format.
 * Example: { supply: { upc: "123" } } => { "supply.upc": "123" }
 */
function flattenDuplicateData(
  data: Record<string, any> | null
): Record<string, any> | null {
  if (!data) return null;

  const flattened: Record<string, any> = {};

  for (const [tableName, tableData] of Object.entries(data)) {
    if (
      tableData &&
      typeof tableData === "object" &&
      !Array.isArray(tableData)
    ) {
      // Flatten nested object properties
      for (const [columnName, value] of Object.entries(tableData)) {
        flattened[`${tableName}.${columnName}`] = value;
      }
    } else {
      // Handle non-object values directly (though unlikely in this use case)
      flattened[tableName] = tableData;
    }
  }

  return flattened;
}

// Main Component
export default React.memo(function ServerDataGrid({
  initialStateOverride,
  columns,
  apiEndpoint,
  tableName,
  enableCreate = false,
  enableEdit = true,
  enableDelete = true,
  enableSearch = true,
  enableDuplicate = false,
  createFormFields,
  defaultPageSize = 50,
  pageSizeOptions = [25, 50, 100, 250, 500],
  height = "calc(100vh - 200px)",
  getRowId = (row) => row.id,
  onError,
  onSuccess,
  columnMenuConfig,
  getDetailPanelContent,
  columnVisibilityModel,
  submitOnlyChangedFields = true,
  onBeforeUpdate,
  onCreateClick,
  onDuplicateClick,
  ExtraButtonsPanel,
  extraButtonsPanelProps,
  apiRef: externalApiRef,
  externalSearchValue,
  ContextMenuComponent,
  defaultEditFieldVisibility,
  enableRowGrouping = true,
  defaultRowGroupingModel = [],
  rowGroupingColumnMode = "single",
}: ServerDataGridProps) {
  const isFirstRender = useIsFirstRender();
  const { settings } = useDataGridSettings();
  const apiRef = useGridApiRef();
  const apiUrl = `${apiBase}${apiEndpoint}`;

  // ALT+C: copy selected rows to clipboard with column headers prepended,
  // using getDataAsCsv so formatting matches MUI X's built-in CTRL+C output.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.altKey || e.key.toLowerCase() !== "c") return;
      e.preventDefault();

      const api = apiRef.current;
      if (!api) return;

      const selectionState = api.state.rowSelection as { ids: Set<GridRowId> };
      const selectedIds = Array.from(selectionState.ids);
      if (selectedIds.length === 0) return;

      const tsvData = api.getDataAsCsv({
        getRowsToExport: () => selectedIds,
        delimiter: "\t",
      });
      navigator.clipboard.writeText(tsvData);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [apiRef]);

  // Sync the internal apiRef with the external one if provided
  useEffect(() => {
    if (externalApiRef && apiRef.current) {
      externalApiRef.current = apiRef.current;
    }
  }, [externalApiRef, apiRef]);

  // Initialize URL state manager
  const urlStateManager = useMemo(
    () => new DataGridUrlStateManager(tableName),
    [tableName]
  );

  // State that remains in the main component
  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<
    Set<GridRowId>
  >(new Set());
  const [fieldOptions, setFieldOptions] = useState<
    Record<string, SelectOption[]>
  >({});
  const [density, setDensity] = useState<
    "compact" | "standard" | "comfortable"
  >(() => {
    const stored = localStorage.getItem(`sdg-density:${tableName}`);
    return (stored as "compact" | "standard" | "comfortable") ?? "standard";
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Stashed filter state — persists across filter panel mount/unmount cycles
  const savedBasicFiltersRef = React.useRef<GridFilterModel | null>(null);
  const savedAdvancedFiltersRef = React.useRef<AdvancedFilterModel | null>(
    null
  );
  // Field to pre-populate when the filter panel next opens via the column menu
  const pendingFilterFieldRef = React.useRef<string | null>(null);
  const [filterPanelMode, setFilterPanelMode] = useState<"basic" | "advanced">(
    () =>
      urlStateManager.readAdvancedFilterModelFromUrl() ? "advanced" : "basic"
  );

  // Advanced filter state — stored outside MUI X so arbitrary AND/OR groups can be sent to the backend
  const [advancedFilterModel, setAdvancedFilterModel] =
    useState<AdvancedFilterModel | null>(() =>
      urlStateManager.readAdvancedFilterModelFromUrl()
    );
  // Ref for synchronous access inside getRows (avoids stale closure)
  const advancedFilterModelRef = React.useRef<AdvancedFilterModel | null>(
    urlStateManager.readAdvancedFilterModelFromUrl()
  );
  // Keep ref in sync with state
  useEffect(() => {
    advancedFilterModelRef.current = advancedFilterModel;
  }, [advancedFilterModel]);

  // Column visibility overrides - divergences from default columnVisibilityModel
  // hiddenColumns: columns visible by default but user hid
  // shownColumns: columns hidden by default but user showed
  const [manuallyHiddenColumns, setManuallyHiddenColumns] = useState<string[]>(
    () => urlStateManager.readHiddenColumnsFromUrl()
  );
  const [manuallyShownColumns, setManuallyShownColumns] = useState<string[]>(
    () => urlStateManager.readShownColumnsFromUrl()
  );

  // Row grouping state - initialize from URL or fall back to default
  const [rowGroupingModel, setRowGroupingModel] = useState<string[]>(() => {
    const fromUrl = urlStateManager.readRowGroupingModelFromUrl();
    return fromUrl ?? defaultRowGroupingModel;
  });

  // Custom hooks
  const { searchValue, searchQueryRef, handleSearchChange } =
    useDebouncedSearch({
      urlStateManager,
      apiRef,
    });

  useGridUrlSync({
    apiRef,
    urlStateManager,
  });

  const {
    rowModesModel,
    setRowModesModel,
    originalRowsRef,
    handleEditClick,
    handleSaveClick,
    handleCancelClick,
    handleRowModesModelChange,
    handleRowEditStop,
    handleCellKeyDown,
  } = useRowEditing({ apiRef });

  // Ref mirror of rowModesModel so getActions can read edit-mode state
  // without making rowModesModel a dependency of columnsWithActions (which
  // would regenerate the columns array — and reset drag-reordered positions —
  // every time a row enters or exits edit mode).
  const rowModesModelRef = React.useRef(rowModesModel);
  useEffect(() => {
    rowModesModelRef.current = rowModesModel;
  }, [rowModesModel]);

  const {
    rowSelectionModel,
    contextMenuState,
    handleRowSelectionModelChange,
    handleRowClick,
    handleContextMenu,
    closeContextMenu,
    getSelectedRows,
  } = useRowSelection({
    apiRef,
  });

  const {
    dataSource,
    loading,
    crudHandlerRef,
    handleRefresh,
    handleDeleteClick,
    handleBulkUpdate,
    handleBulkSet,
    wrappedGetRowId,
    handleGroupPaginationChange,
    groupPaginationState,
  } = useServerDataSource({
    apiUrl,
    getRowId,
    searchQueryRef,
    originalRowsRef,
    rowModesModel,
    setRowModesModel,
    setFieldOptions,
    setSnackbar,
    apiRef,
    onSuccess,
    onError,
    submitOnlyChangedFields,
    defaultPageSize,
    onBeforeUpdate,
    advancedFilterModelRef,
  });

  const {
    createModalOpen,
    setCreateModalOpen,
    duplicateData,
    handleAddClick,
    handleDuplicateClick,
    handleCreateSuccess,
    handleCreateError,
  } = useCreateFormModal({
    apiRef,
    rowSelectionModel,
    setSnackbar,
    onRefresh: handleRefresh,
    onSuccess,
    onError,
    onCreateClick,
    onDuplicateClick,
    flattenDuplicateData,
  });

  // Sync external search value — skip empty string to avoid clobbering a
  // URL-initialized search value when the parent defaults externalSearchValue=""
  useEffect(() => {
    if (externalSearchValue && !isFirstRender) {
      handleSearchChange(externalSearchValue);
    }
  }, [externalSearchValue, handleSearchChange, isFirstRender]);

  // Single-row update handler for the edit form (wraps crudHandlerRef)
  const handleSingleUpdate = useCallback(
    async (rowId: any, data: Record<string, any>) => {
      const result = await crudHandlerRef.current.sendUpdateRequest(data);
      return { success: result.success, error: result.error };
    },
    [crudHandlerRef]
  );

  // Row grouping model change handler
  const handleRowGroupingModelChange = useCallback(
    (newModel: string[]) => {
      setRowGroupingModel(newModel);
      // Sync to URL
      urlStateManager.writeRowGroupingModelToUrl(newModel);
      // Refresh data when grouping changes
      if (apiRef.current) {
        apiRef.current.dataSource.fetchRows();
      }
    },
    [apiRef, urlStateManager]
  );

  // Helper: check if a column is hidden by default in the prop-level visibility model
  const isHiddenByDefault = useCallback(
    (field: string): boolean => {
      return columnVisibilityModel?.[field] === false;
    },
    [columnVisibilityModel]
  );

  // Sync divergences to URL
  const syncVisibilityToUrl = useCallback(
    (hidden: string[], shown: string[]) => {
      urlStateManager.writeColumnVisibilityToUrl(hidden, shown);
    },
    [urlStateManager]
  );

  // Toggle a single column's visibility
  const handleToggleColumn = useCallback(
    (field: string, visible: boolean) => {
      if (visible) {
        // User wants to show this column
        if (isHiddenByDefault(field)) {
          // It's hidden by default → add to shownColumns (divergence)
          setManuallyShownColumns((prev) => {
            if (prev.includes(field)) return prev;
            const next = [...prev, field];
            syncVisibilityToUrl(manuallyHiddenColumns, next);
            return next;
          });
        } else {
          // It's visible by default → remove from hiddenColumns (revert divergence)
          setManuallyHiddenColumns((prev) => {
            const next = prev.filter((f) => f !== field);
            syncVisibilityToUrl(next, manuallyShownColumns);
            return next;
          });
        }
      } else {
        // User wants to hide this column
        if (isHiddenByDefault(field)) {
          // It's hidden by default → remove from shownColumns (revert divergence)
          setManuallyShownColumns((prev) => {
            const next = prev.filter((f) => f !== field);
            syncVisibilityToUrl(manuallyHiddenColumns, next);
            return next;
          });
        } else {
          // It's visible by default → add to hiddenColumns (divergence)
          setManuallyHiddenColumns((prev) => {
            if (prev.includes(field)) return prev;
            const next = [...prev, field];
            syncVisibilityToUrl(next, manuallyShownColumns);
            return next;
          });
        }
      }
    },
    [
      isHiddenByDefault,
      manuallyHiddenColumns,
      manuallyShownColumns,
      syncVisibilityToUrl,
    ]
  );

  // Toggle all columns visible or hidden
  const handleToggleAllColumns = useCallback(
    (visible: boolean) => {
      if (visible) {
        // Show all: clear all hidden overrides, add shown overrides for default-hidden columns
        const defaultHiddenFields = columns
          .filter((col) => isHiddenByDefault(col.field))
          .map((col) => col.field);
        setManuallyHiddenColumns([]);
        setManuallyShownColumns(defaultHiddenFields);
        syncVisibilityToUrl([], defaultHiddenFields);
      } else {
        // Hide all: clear all shown overrides, add hidden overrides for default-visible columns
        const defaultVisibleFields = columns
          .filter(
            (col) =>
              !isHiddenByDefault(col.field) &&
              col.field !== "actions" &&
              col.field !== "__detail_panel_toggle__" &&
              col.hideable !== false
          )
          .map((col) => col.field);
        setManuallyHiddenColumns(defaultVisibleFields);
        setManuallyShownColumns([]);
        syncVisibilityToUrl(defaultVisibleFields, []);
      }
    },
    [columns, isHiddenByDefault, syncVisibilityToUrl]
  );

  // Reset column visibility to default (clear all user overrides)
  const handleResetColumnVisibility = useCallback(() => {
    setManuallyHiddenColumns([]);
    setManuallyShownColumns([]);
    syncVisibilityToUrl([], []);
  }, [syncVisibilityToUrl]);

  // Hide column from column menu (used by ColumnMenu component)
  const handleHideColumn = useCallback(
    (field: string) => {
      handleToggleColumn(field, false);
    },
    [handleToggleColumn]
  );

  // Clear groups handler
  const handleClearGroups = useCallback(() => {
    handleRowGroupingModelChange([]);
  }, [handleRowGroupingModelChange]);

  // Advanced filter apply/clear handler
  const handleAdvancedFilterModelChange = useCallback(
    (model: AdvancedFilterModel | null) => {
      setAdvancedFilterModel(model);
      advancedFilterModelRef.current = model;
      urlStateManager.writeAdvancedFilterModelToUrl(model);
      // Trigger data refetch via dataSource
      if (apiRef.current?.dataSource) {
        apiRef.current.dataSource.fetchRows();
      }
    },
    [urlStateManager, apiRef]
  );

  // On mount: if an advanced filter was restored from URL, clear MUI X's flat
  // filter state so the two systems don't appear to coexist.
  useEffect(() => {
    if (advancedFilterModel && apiRef.current) {
      apiRef.current.setFilterModel({ items: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toolbar action handlers
  const handleFilterClick = useCallback(() => {
    apiRef.current?.showFilterPanel?.();
  }, [apiRef]);

  // Column menu "Filter" — pre-populates the active filter panel with the clicked column
  const handleFilterColumn = useCallback(
    (field: string) => {
      if (filterPanelMode === "basic") {
        // Build on top of the current basic filter state (stash takes precedence
        // because MUI X's filter state is cleared when switching to advanced mode)
        const currentMuiModel: GridFilterModel = apiRef.current?.exportState()
          ?.filter?.filterModel ?? { items: [] };
        const baseModel: GridFilterModel =
          savedBasicFiltersRef.current ?? currentMuiModel;

        const alreadyHasField = baseModel.items.some(
          (item) => item.field === field
        );
        if (!alreadyHasField) {
          const newModel: GridFilterModel = {
            ...baseModel,
            items: [
              ...baseModel.items,
              // operator "" means the row is visible but not yet applied —
              // the user still needs to pick an operator and enter a value
              { id: Date.now(), field, operator: "" },
            ],
          };
          // Keep the stash in sync so the panel's mount effect doesn't overwrite
          savedBasicFiltersRef.current = newModel;
          apiRef.current?.setFilterModel(newModel);
        }
        apiRef.current?.showFilterPanel(field);
      } else {
        // Advanced mode: set the pending field so FilterBuilderContent picks it
        // up on mount and pre-adds an item row for that column
        pendingFilterFieldRef.current = field;
        apiRef.current?.showFilterPanel();
      }
    },
    [filterPanelMode, apiRef, savedBasicFiltersRef]
  );

  const handleExportClick = useCallback(() => {
    apiRef.current?.exportDataAsCsv();
  }, [apiRef]);

  const handleDensityChange = useCallback(
    (newDensity: "compact" | "standard" | "comfortable") => {
      setDensity(newDensity);
      localStorage.setItem(`sdg-density:${tableName}`, newDensity);
    },
    [tableName]
  );

  const handleGridRefresh = useCallback(() => {
    if (apiRef.current?.dataSource) {
      apiRef.current.dataSource.fetchRows();
    }
  }, [apiRef]);

  // Detail panel control
  const handleDetailPanelExpansionChange = useCallback<
    NonNullable<DataGridPremiumProps["onDetailPanelExpandedRowIdsChange"]>
  >(
    (newExpandedRowIds) => {
      setDetailPanelExpandedRowIds(newExpandedRowIds);
    },
    [setDetailPanelExpandedRowIds]
  );

  // Add actions column if CRUD operations are enabled
  const columnsWithActions: GridColDef[] = useMemo(() => {
    const actionColumn: GridColDef = {
      field: "actions",
      type: "actions" as const,
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: (params: GridRowParams) => {
        const isInEditMode =
          rowModesModelRef.current[params.id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              onClick={handleSaveClick(params.id)}
              color="primary"
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              onClick={handleCancelClick(params.id)}
              color="inherit"
            />,
          ];
        }

        const actions = [];
        if (enableEdit) {
          actions.push(
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              onClick={handleEditClick(params.id)}
              color="inherit"
            />
          );
        }
        if (enableDelete) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={handleDeleteClick(params.id)}
              color="inherit"
            />
          );
        }
        return actions;
      },
    };

    if (!(enableEdit || enableDelete)) return [...columns];

    return settings.actionsColumnPosition === "front"
      ? [actionColumn, ...columns]
      : [...columns, actionColumn];
  }, [
    columns,
    enableEdit,
    enableDelete,
    handleSaveClick,
    handleCancelClick,
    handleEditClick,
    handleDeleteClick,
    settings.actionsColumnPosition,
  ]);

  // When stacking is enabled, inject a renderHeader that places each word on its own line.
  // Only affects columns without a custom renderHeader and with multi-word headerNames.
  const processedColumns = useMemo((): GridColDef[] => {
    if (!settings.stackColumnHeaders) return columnsWithActions;
    return columnsWithActions.map((col) => {
      if (col.renderHeader || !col.headerName) return col;
      const words = col.headerName.trim().split(/\s+/);
      if (words.length < 2) return col;
      return {
        ...col,
        renderHeader: () => (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              lineHeight: 1.2,
              fontWeight: 500,
            }}
          >
            {words.map((word, i) => (
              <span key={i}>{word}</span>
            ))}
          </Box>
        ),
      };
    });
  }, [columnsWithActions, settings.stackColumnHeaders]);

  // Compute default sort model from the first column with a non-empty headerName
  const defaultSortModel = useMemo(() => {
    const firstSortableColumn = columns.find(
      (col) => col.headerName && col.headerName.trim() !== ""
    );
    if (firstSortableColumn) {
      return [{ field: firstSortableColumn.field, sort: "asc" as const }];
    }
    return [];
  }, [columns]);

  // Read initial state from URL and merge with initialStateOverride
  const mergedInitialState = useMemo(() => {
    const filterModelFromUrl = urlStateManager.readFilterModelFromUrl();
    const sortModelFromUrl = urlStateManager.readSortModelFromUrl();

    // Determine the effective sort model: URL > initialStateOverride > default
    const effectiveSortModel =
      sortModelFromUrl ||
      initialStateOverride?.sorting?.sortModel ||
      (defaultSortModel.length > 0 ? defaultSortModel : undefined);

    // Start with URL-based state
    const urlState: GridInitialState = {
      filter: filterModelFromUrl
        ? { filterModel: filterModelFromUrl }
        : undefined,
      sorting: effectiveSortModel
        ? { sortModel: effectiveSortModel }
        : undefined,
    };

    // If no initialStateOverride, return URL state
    if (!initialStateOverride) {
      return urlState;
    }

    // Merge initialStateOverride with URL state
    const merged: GridInitialState = {
      ...initialStateOverride,
    };

    // Merge filter models: combine URL filters with override filters
    if (filterModelFromUrl || initialStateOverride.filter?.filterModel) {
      const urlItems = filterModelFromUrl?.items || [];
      const overrideItems =
        initialStateOverride.filter?.filterModel?.items || [];

      // Combine both filter arrays, URL filters take precedence for same fields
      const combinedItems = [...overrideItems];
      const overrideFields = new Set(overrideItems.map((item) => item.field));

      // Add URL filters that don't conflict with override filters
      urlItems.forEach((urlItem) => {
        if (!overrideFields.has(urlItem.field)) {
          combinedItems.push(urlItem);
        } else {
          // URL filter overrides the initialStateOverride for this field
          const index = combinedItems.findIndex(
            (item) => item.field === urlItem.field
          );
          if (index !== -1) {
            combinedItems[index] = urlItem;
          }
        }
      });

      if (combinedItems.length > 0) {
        merged.filter = {
          ...initialStateOverride.filter,
          filterModel: {
            items: combinedItems,
          },
        };
      }
    }

    // Apply the effective sort model
    if (effectiveSortModel) {
      merged.sorting = {
        ...initialStateOverride.sorting,
        sortModel: effectiveSortModel,
      };
    }

    return merged;
  }, [urlStateManager, initialStateOverride, defaultSortModel]);

  // Merge columnVisibilityModel with user overrides (divergences)
  const mergedColumnVisibilityModel = useMemo(() => {
    const manualHides = manuallyHiddenColumns.reduce(
      (acc, field) => ({ ...acc, [field]: false }),
      {} as Record<string, boolean>
    );
    const manualShows = manuallyShownColumns.reduce(
      (acc, field) => ({ ...acc, [field]: true }),
      {} as Record<string, boolean>
    );
    return {
      ...columnVisibilityModel,
      ...manualHides,
      ...manualShows,
      [GRID_DETAIL_PANEL_TOGGLE_COL_DEF.field]: false,
    };
  }, [columnVisibilityModel, manuallyHiddenColumns, manuallyShownColumns]);

  // Create options context value
  const optionsContextValue: OptionsContextValue = useMemo(
    () => ({
      options: fieldOptions,
      getOptions: (field: string) => fieldOptions[field] || [],
    }),
    [fieldOptions]
  );

  // Grouping column definition with inline pagination controls
  const groupingColDef = useMemo(
    () => ({
      renderCell: (params: any) => {
        if (params.rowNode.type !== "group") {
          return "";
        }
        return (
          <GroupPaginationCell
            {...params}
            groupPaginationState={groupPaginationState}
            pageSizeOptions={pageSizeOptions}
            onPaginationChange={handleGroupPaginationChange}
          />
        );
      },
      // MUI bug workaround-- fixes issues when editing with 2+ grouped cols active
      valueFormatter: (value: any) => value ?? "",
      minWidth: 300,
    }),
    [groupPaginationState, pageSizeOptions, handleGroupPaginationChange]
  );

  const toolbarSlotProps = useMemo(
    () => ({
      onAddClick: handleAddClick,
      onDuplicateClick: handleDuplicateClick,
      onRefreshClick: handleRefresh,
      enableCreate,
      enableDuplicate,
      selectedRowCount: rowSelectionModel.ids.size,
      searchValue: searchValue,
      onSearchChange: handleSearchChange,
      enableSearch,
      onFilterClick: handleFilterClick,
      onExportClick: handleExportClick,
      density,
      onDensityChange: handleDensityChange,
      apiUrl,
      globalSearchValue: searchValue,
      onClearGroups: enableRowGrouping ? handleClearGroups : undefined,
      columns,
      columnVisibilityModel: mergedColumnVisibilityModel,
      onToggleColumn: handleToggleColumn,
      onToggleAllColumns: handleToggleAllColumns,
      onResetColumnVisibility: handleResetColumnVisibility,
      advancedFilterCount: countAdvancedFilterItems(advancedFilterModel),
      advancedFilterModel,
      onClearAdvancedFilter: () => handleAdvancedFilterModelChange(null),
    }),
    [
      handleAddClick,
      handleDuplicateClick,
      handleRefresh,
      enableCreate,
      enableDuplicate,
      rowSelectionModel.ids.size,
      searchValue,
      handleSearchChange,
      enableSearch,
      handleFilterClick,
      handleExportClick,
      density,
      handleDensityChange,
      apiUrl,
      enableRowGrouping,
      handleClearGroups,
      columns,
      mergedColumnVisibilityModel,
      handleToggleColumn,
      handleToggleAllColumns,
      handleResetColumnVisibility,
      advancedFilterModel,
      handleAdvancedFilterModelChange,
    ]
  );

  return (
    <OptionsContext.Provider value={optionsContextValue}>
      <Box
        sx={{
          height,
          display: "flex",
          flexDirection: "column",
          "--sdg-odd-row-color": settings.alternatingRows
            ? settings.oddRowColor
            : settings.evenRowColor,
          "--sdg-even-row-color": settings.evenRowColor,
        }}
      >
        {/* Render Extra Buttons Panel if provided */}
        {ExtraButtonsPanel && (
          <ExtraButtonsPanel
            rowSelectionModel={rowSelectionModel}
            setSnackbar={setSnackbar}
            apiRef={apiRef}
            getSelectedRows={getSelectedRows}
            onBulkUpdate={handleBulkUpdate}
            {...extraButtonsPanelProps}
          />
        )}
        <DataGridPremium
          sx={{
            flex: 1,
            "& .MuiDataGrid-columnHeader": {
              paddingLeft: `${settings.cellPadding}px`,
              paddingRight: `${settings.cellPadding}px`,
            },
            "& .MuiDataGrid-cell": {
              paddingLeft: `${settings.cellPadding}px`,
              paddingRight: `${settings.cellPadding}px`,
            },
          }}
          className="server-data-grid"
          label={tableName}
          apiRef={apiRef}
          columnHeaderHeight={80}
          columns={processedColumns}
          loading={loading}
          getRowId={wrappedGetRowId}
          columnVisibilityModel={mergedColumnVisibilityModel}
          // Detail panel
          detailPanelExpandedRowIds={detailPanelExpandedRowIds}
          getDetailPanelContent={
            getDetailPanelContent
              ? (params) => getDetailPanelContent(params, apiRef)
              : undefined
          }
          filterDebounceMs={500}
          onDetailPanelExpandedRowIdsChange={handleDetailPanelExpansionChange}
          // Pagination
          pagination
          pageSizeOptions={pageSizeOptions}
          initialState={{
            pagination: {
              paginationModel: { pageSize: defaultPageSize, page: 0 },
              rowCount: 0,
            },
            ...mergedInitialState,
          }}
          // DataSource
          dataSourceCache={null}
          dataSource={dataSource}
          onDataSourceError={(error) => {
            if (error instanceof GridGetRowsError) {
              console.error("Row fetch error:", error);
            }
            if (error instanceof GridUpdateRowError) {
              console.error("Row update error:", error);
            }
          }}
          // Editing
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          onCellKeyDown={handleCellKeyDown}
          // Selection
          checkboxSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          onRowClick={handleRowClick}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
          }
          // Density
          density={density}
          // Row Grouping (conditional)
          {...(enableRowGrouping && {
            rowGroupingModel: rowGroupingModel,
            onRowGroupingModelChange: handleRowGroupingModelChange,
            rowGroupingColumnMode: rowGroupingColumnMode,
            groupingColDef: groupingColDef,
          })}
          // Toolbar and Column Menu
          showToolbar
          slots={{
            toolbar: CustomToolbar,
            columnMenu: CustomColumnMenu,
            filterPanel: CombinedFilterPanel,
          }}
          slotProps={{
            toolbar: toolbarSlotProps,
            columnMenu: {
              ...columnMenuConfig,
              showGrouping: enableRowGrouping,
              onHideColumn: handleHideColumn,
              onFilterColumn: handleFilterColumn,
            },
            row: {
              onContextMenu: handleContextMenu,
            },
            filterPanel: {
              advancedFilterModel,
              onAdvancedApply: (model: AdvancedFilterModel) => {
                apiRef.current?.setFilterModel({ items: [] });
                handleAdvancedFilterModelChange(model);
              },
              onAdvancedClear: () => {
                handleAdvancedFilterModelChange(null);
                apiRef.current?.setFilterModel({ items: [] });
              },
              filterColumns: columns,
              savedBasicFiltersRef,
              savedAdvancedFiltersRef,
              pendingFilterFieldRef,
              mode: filterPanelMode,
              onModeChange: setFilterPanelMode,
            },
          }}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Create Form Modal */}
        {createFormFields && (
          <CreateFormModal
            open={createModalOpen}
            onClose={() => setCreateModalOpen(false)}
            fields={createFormFields}
            apiUrl={apiUrl}
            onSuccess={handleCreateSuccess}
            onError={handleCreateError}
            duplicateData={duplicateData}
            fieldOptions={fieldOptions}
          />
        )}

        {/* Context Menu */}
        {ContextMenuComponent ? (
          <ContextMenuComponent
            open={contextMenuState.open}
            event={contextMenuState.event}
            closeContextMenu={closeContextMenu}
            selectedRows={contextMenuState.selectedRows}
            onGridRefresh={handleGridRefresh}
            columns={columns}
            tableName={tableName}
            apiRef={apiRef}
            onSingleUpdate={handleSingleUpdate}
            onBulkSet={handleBulkSet}
            fieldOptions={fieldOptions}
            getRowId={wrappedGetRowId}
            defaultEditFieldVisibility={defaultEditFieldVisibility}
          />
        ) : (
          <BaseContextMenu
            open={contextMenuState.open}
            event={contextMenuState.event}
            closeContextMenu={closeContextMenu}
            selectedRows={contextMenuState.selectedRows}
            onGridRefresh={handleGridRefresh}
            columns={columns}
            tableName={tableName}
            apiRef={apiRef}
            onSingleUpdate={handleSingleUpdate}
            onBulkSet={handleBulkSet}
            fieldOptions={fieldOptions}
            getRowId={wrappedGetRowId}
            defaultEditFieldVisibility={defaultEditFieldVisibility}
          />
        )}
      </Box>
    </OptionsContext.Provider>
  );
});
